"use client"

import { useState, useEffect, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Search, Loader2, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react"
import { useCommissionPlanApi } from "@/hooks"
import { useTranslation } from "@/hooks/use-translation"
import { CommissionPlanConfiguration } from "@/models/interface"
import { CommissionPlanStatus } from "@/models/enum"
import { getMostRecentPlans, formatDate } from "@/lib/utils"
import { useRouter } from "next/navigation"

export function CommissionPlanList() {
  const { t } = useTranslation()
  const router = useRouter()
  const { fetchCommissionPlans } = useCommissionPlanApi()
  const [commissionPlans, setCommissionPlans] = useState<CommissionPlanConfiguration[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState("")
  const [sortColumn, setSortColumn] = useState<'status' | 'effectiveDate' | 'lastUpdated' | null>('effectiveDate')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  
  // Fetch commission plans on mount
  useEffect(() => {
    const loadCommissionPlans = async () => {
      setIsLoading(true)
      try {
        const plans = await fetchCommissionPlans()
        if (plans) {
          // Filter to get only the most recent versions
          const mostRecentPlans = getMostRecentPlans(plans)
          setCommissionPlans(mostRecentPlans)
        }
      } catch (error) {
        console.error('Error loading commission plans:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadCommissionPlans()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Handle column sorting
  const handleSort = (column: 'status' | 'effectiveDate' | 'lastUpdated') => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to descending
      setSortColumn(column)
      setSortDirection('desc')
    }
  }

  // Render sort icon based on current sort state
  const renderSortIcon = (column: 'status' | 'effectiveDate' | 'lastUpdated') => {
    if (sortColumn !== column) {
      return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 ml-1" />
      : <ArrowDown className="w-4 h-4 ml-1" />
  }

  // Filter and sort plans based on search query
  const filteredPlans = useMemo(() => {
    let plans = commissionPlans
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      plans = plans.filter((plan) => {
        const name = plan.Data.Name?.toLowerCase() || ''
        const description = plan.Data.Description?.toLowerCase() || ''
        const status = plan.Data.Status?.toLowerCase() || ''
        
        return name.includes(query) || description.includes(query) || status.includes(query)
      })
    }
    
    // Sort based on selected column and direction
    return [...plans].sort((a, b) => {
      let comparison = 0
      
      if (sortColumn === 'status') {
        const statusA = a.Data.Status?.toLowerCase() || ''
        const statusB = b.Data.Status?.toLowerCase() || ''
        comparison = statusA.localeCompare(statusB)
      } else if (sortColumn === 'effectiveDate') {
        const dateA = new Date(a.Data.EffectiveDate).getTime()
        const dateB = new Date(b.Data.EffectiveDate).getTime()
        comparison = dateA - dateB
      } else if (sortColumn === 'lastUpdated') {
        const dateA = new Date(a.LastModifiedDateTime || 0).getTime()
        const dateB = new Date(b.LastModifiedDateTime || 0).getTime()
        comparison = dateA - dateB
      }
      
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [commissionPlans, searchQuery, sortColumn, sortDirection])

  // Navigate to create new plan page
  const handleNewPlan = () => {
    // Preserve the token in the URL when navigating
    const currentParams = new URLSearchParams(window.location.search)
    const token = currentParams.get('token')
    const targetUrl = token ? `/create?token=${token}` : '/create'
    router.push(targetUrl)
  }

  // Handle plan row click
  const handlePlanClick = (plan: CommissionPlanConfiguration) => {
    // Navigate to view/edit page with commission plan ID as query parameter
    const currentParams = new URLSearchParams(window.location.search)
    const token = currentParams.get('token')
    const targetUrl = token 
      ? `/view?id=${plan.Data.CommissionPlanId}&token=${token}` 
      : `/view?id=${plan.Data.CommissionPlanId}`
    router.push(targetUrl)
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header with search and actions */}
        <div className="flex items-center justify-between gap-4">
          <Button 
            onClick={handleNewPlan}
            className="bg-slate-800 hover:bg-slate-700 text-white rounded-full px-6"
          >
            {t('listPage.newPlan')}
          </Button>
          
          <div className="flex items-center gap-2 flex-1 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder={t('listPage.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 bg-white border-gray-200 rounded-lg"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
              <span className="ml-3 text-gray-600">{t('listPage.loading')}</span>
            </div>
          ) : filteredPlans.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>{t('listPage.noPlans')}</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[calc(100vh-200px)]">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-gray-50">
                  <TableRow className="bg-gray-50 hover:bg-gray-50 border-b">
                    <TableHead className="font-semibold text-gray-700 border-r bg-gray-50">
                      {t('listPage.table.name')}
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 border-r text-center cursor-pointer hover:bg-gray-100 bg-gray-50"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center justify-center">
                        {t('listPage.table.status')}
                        {renderSortIcon('status')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 border-r text-center cursor-pointer hover:bg-gray-100 bg-gray-50"
                      onClick={() => handleSort('effectiveDate')}
                    >
                      <div className="flex items-center justify-center">
                        {t('listPage.table.effectiveDate')}
                        {renderSortIcon('effectiveDate')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="font-semibold text-gray-700 text-center cursor-pointer hover:bg-gray-100 bg-gray-50"
                      onClick={() => handleSort('lastUpdated')}
                    >
                      <div className="flex items-center justify-center">
                        {t('listPage.table.lastUpdated')}
                        {renderSortIcon('lastUpdated')}
                      </div>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPlans.map((plan) => (
                    <TableRow
                      key={plan.id || plan.Data.CommissionPlanId}
                      onClick={() => handlePlanClick(plan)}
                      className="cursor-pointer hover:bg-gray-50 border-b"
                    >
                      <TableCell className="border-r">
                        <div className="flex flex-col max-w-md">
                          <span className="font-medium text-gray-900">
                            {plan.Data.Name}
                          </span>
                          {plan.Data.Description && (
                            <span className="text-sm text-gray-500 mt-1 line-clamp-1">
                              {plan.Data.Description}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="border-r text-center">
                        <div className="flex justify-center">
                          <Badge
                            variant={
                              plan.Data.Status?.toLowerCase() === CommissionPlanStatus.ACTIVE.toLowerCase()
                                ? "success"
                                : plan.Data.Status?.toLowerCase() === CommissionPlanStatus.INACTIVE.toLowerCase()
                                ? "inactive"
                                : plan.Data.Status?.toLowerCase() === CommissionPlanStatus.REPLACED.toLowerCase()
                                ? "replaced"
                                : "draft"
                            }
                            className="px-12 py-2.5 text-sm font-normal min-w-[200px] justify-center"
                          >
                            {plan.Data.Status?.toLowerCase() === CommissionPlanStatus.ACTIVE.toLowerCase()
                              ? t('listPage.status.active')
                              : plan.Data.Status?.toLowerCase() === CommissionPlanStatus.INACTIVE.toLowerCase()
                              ? t('listPage.status.inactive')
                              : plan.Data.Status?.toLowerCase() === CommissionPlanStatus.REPLACED.toLowerCase()
                              ? t('listPage.status.replaced')
                              : t('listPage.status.draft')}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-700 border-r text-center">
                        {formatDate(plan.Data.EffectiveDate)}
                      </TableCell>
                      <TableCell className="text-gray-700 text-center">
                        {plan.LastModifiedDateTime
                          ? formatDate(plan.LastModifiedDateTime)
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

